/**
 * Click or drag-to-size placement for `section` nodes. Commits via `onAddNode`
 * from the page; adoption of intersecting nodes runs after the section is added.
 */

import { Node, useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SNAP } from "../canvas-controls.config";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasSectionPreview } from "../context/CanvasSectionPreviewContext";
import { DEFAULT_SECTION_COLOR } from "../nodes/shared/CanvasColorPalettes";
import {
  boundsFromRect,
  buildParentUpdatesForAdoption,
  getAdoptionCandidates,
  getSectionPlacementPreviewMemberIds,
  refineAdoptionCandidatesForSectionNesting,
} from "../utils/sectionMembership";

const snapToGrid = (val: number, step: number) => Math.round(val / step) * step;

const MIN_DRAG_PX = 40;
/** Default section dimensions for a single click (centered on the cursor). */
const DEFAULT_WIDTH = 544;
const DEFAULT_HEIGHT = 368;
const MIN_SIZE = 50;

function buildRubberBandRect(
  dragStart: { x: number; y: number },
  flowPos: { x: number; y: number },
  snapX: number,
  snapY: number,
): SectionPlacementGhost {
  const x = snapToGrid(
    Math.min(dragStart.x, snapToGrid(flowPos.x, snapX)),
    snapX,
  );
  const y = snapToGrid(
    Math.min(dragStart.y, snapToGrid(flowPos.y, snapY)),
    snapY,
  );
  const width = Math.max(
    MIN_SIZE,
    snapToGrid(Math.abs(flowPos.x - dragStart.x), snapX),
  );
  const height = Math.max(
    MIN_SIZE,
    snapToGrid(Math.abs(flowPos.y - dragStart.y), snapY),
  );
  return { position: { x, y }, width, height };
}

function centeredClickPlacement(
  clickX: number,
  clickY: number,
  snapX: number,
  snapY: number,
): SectionPlacementGhost {
  return {
    position: {
      x: snapToGrid(clickX - DEFAULT_WIDTH / 2, snapX),
      y: snapToGrid(clickY - DEFAULT_HEIGHT / 2, snapY),
    },
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
  };
}

export interface SectionPlacementGhost {
  position: { x: number; y: number };
  width: number;
  height: number;
}

interface UseSectionPlacementOptions {
  onAfterSectionAdded?: (
    sectionNode: Node,
    ghost: SectionPlacementGhost,
  ) => void;
}

export function useSectionPlacement({
  onAfterSectionAdded,
}: UseSectionPlacementOptions = {}) {
  const reactFlowInstance = useReactFlow();
  const { screenToFlowPosition, setNodes, getNodes, getInternalNode } =
    reactFlowInstance;
  const { addNode } = useCanvas();
  const { setSectionPreview } = useCanvasSectionPreview();

  const [isPlacingSection, setIsPlacingSection] = useState(false);
  /** Rubber-band preview while dragging; null before drag and after release. */
  const [ghost, setGhost] = useState<SectionPlacementGhost | null>(null);

  const screenToFlowRef = useRef(screenToFlowPosition);
  screenToFlowRef.current = screenToFlowPosition;
  const addNodeRef = useRef(addNode);
  addNodeRef.current = addNode;
  const onAfterSectionAddedRef = useRef(onAfterSectionAdded);
  onAfterSectionAddedRef.current = onAfterSectionAdded;
  const getNodesRef = useRef(getNodes);
  getNodesRef.current = getNodes;
  const getInternalNodeRef = useRef(getInternalNode);
  getInternalNodeRef.current = getInternalNode;
  const setSectionPreviewRef = useRef(setSectionPreview);
  setSectionPreviewRef.current = setSectionPreview;

  const [snapX, snapY] = CANVAS_SNAP.grid;

  const clearPlacementPreview = useCallback(() => {
    setSectionPreviewRef.current(null);
  }, []);

  const startPlacing = useCallback(() => {
    setIsPlacingSection(true);
  }, []);

  const cancelPlacing = useCallback(() => {
    clearPlacementPreview();
    setIsPlacingSection(false);
    setGhost(null);
  }, [clearPlacementPreview]);

  const handleToggleSectionMode = useCallback(() => {
    if (isPlacingSection) {
      cancelPlacing();
    } else {
      startPlacing();
    }
  }, [isPlacingSection, cancelPlacing, startPlacing]);

  useEffect(() => {
    if (!isPlacingSection) {
      setGhost(null);
      clearPlacementPreview();
      return;
    }

    const syncPlacementPreview = (rect: SectionPlacementGhost) => {
      const memberIds = getSectionPlacementPreviewMemberIds(
        rect,
        getNodesRef.current(),
        getInternalNodeRef.current,
      );
      if (memberIds.size > 0) {
        setSectionPreviewRef.current({ memberIds, mode: "place" });
      } else {
        setSectionPreviewRef.current(null);
      }
    };

    let dragStart: { x: number; y: number } | null = null;
    let dragStartScreen: { x: number; y: number } | null = null;

    const getFlow = (e: MouseEvent | PointerEvent) =>
      screenToFlowRef.current({ x: e.clientX, y: e.clientY });

    const isOnPane = (e: MouseEvent | PointerEvent): boolean => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-canvas-control]")) return false;
      return !!target.closest(".react-flow__pane");
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        cancelPlacing();
      }
    };

    // Block React Flow's pointer-based drag-to-select while we own the gesture.
    // CanvasPage also sets selectionOnDrag={false} during placement; stopping
    // pointer events here prevents the selection rectangle from starting even if
    // that prop lags one frame.
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.stopPropagation();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.stopPropagation();
    };

    // Drag-release often lands on an enclosed node; without this, the synthetic
    // click selects that node instead of the section we just added.
    const onClick = (e: MouseEvent) => {
      if (!isOnPane(e)) return;
      e.stopPropagation();
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragStartScreen = { x: e.clientX, y: e.clientY };
      const pos = getFlow(e);
      dragStart = {
        x: snapToGrid(pos.x, snapX),
        y: snapToGrid(pos.y, snapY),
      };
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragStart || !dragStartScreen || e.buttons !== 1) {
        return;
      }
      const dx = e.clientX - dragStartScreen.x;
      const dy = e.clientY - dragStartScreen.y;
      if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;

      const nextGhost = buildRubberBandRect(
        dragStart,
        getFlow(e),
        snapX,
        snapY,
      );
      setGhost(nextGhost);
      syncPlacementPreview(nextGhost);
    };

    const onMouseUp = (e: MouseEvent) => {
      if (e.button !== 0 || !dragStart || !dragStartScreen) {
        dragStart = null;
        dragStartScreen = null;
        setGhost(null);
        clearPlacementPreview();
        return;
      }

      const dx = e.clientX - dragStartScreen.x;
      const dy = e.clientY - dragStartScreen.y;
      const isDrag = Math.abs(dx) >= MIN_DRAG_PX || Math.abs(dy) >= MIN_DRAG_PX;

      let position: { x: number; y: number };
      let width: number;
      let height: number;

      if (isDrag) {
        const dragRect = buildRubberBandRect(
          dragStart,
          getFlow(e),
          snapX,
          snapY,
        );
        position = dragRect.position;
        width = dragRect.width;
        height = dragRect.height;
      } else {
        const clickPlacement = centeredClickPlacement(
          dragStart.x,
          dragStart.y,
          snapX,
          snapY,
        );
        position = clickPlacement.position;
        width = clickPlacement.width;
        height = clickPlacement.height;
      }

      const sectionId = uuidv4();
      const ghostRect = { position, width, height };

      const newNode: Node = {
        id: sectionId,
        type: "section",
        position,
        width,
        height,
        zIndex: -1,
        data: {
          title: "Section",
          sectionColor: DEFAULT_SECTION_COLOR,
          noFill: true,
          borderStyle: "solid",
          borderWidth: 2,
        },
      };

      e.preventDefault();
      e.stopPropagation();

      addNodeRef.current({ ...newNode, selected: true });
      onAfterSectionAddedRef.current?.(newNode, ghostRect);

      dragStart = null;
      dragStartScreen = null;
      setGhost(null);
      clearPlacementPreview();

      // Keep placement mode through the synthetic click, then select only the
      // new section (add does not deselect other nodes in Liveblocks storage).
      requestAnimationFrame(() => {
        setNodes((nds) =>
          nds.map((n) => ({ ...n, selected: n.id === sectionId })),
        );
        setIsPlacingSection(false);
      });
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("pointerup", onPointerUp, { capture: true });
    window.addEventListener("click", onClick, { capture: true });
    window.addEventListener("mousedown", onMouseDown, { capture: true });
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp, { capture: true });

    return () => {
      clearPlacementPreview();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
      window.removeEventListener("pointerup", onPointerUp, { capture: true });
      window.removeEventListener("click", onClick, { capture: true });
      window.removeEventListener("mousedown", onMouseDown, { capture: true });
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp, { capture: true });
    };
  }, [
    isPlacingSection,
    cancelPlacing,
    clearPlacementPreview,
    snapX,
    snapY,
    setNodes,
  ]);

  return {
    isPlacingSection,
    ghost,
    startPlacing,
    cancelPlacing,
    handleToggleSectionMode,
  };
}

type SectionParentUpdate = {
  nodeId: string;
  parentId: string;
  position: { x: number; y: number };
};

type GetInternalNodeFn = (
  id: string,
) => ReturnType<ReturnType<typeof useReactFlow>["getInternalNode"]>;

/**
 * Adoption for a section just placed. Uses placement ghost bounds for
 * intersection and the committed section node for parent bounds so adoption
 * does not depend on React Flow already listing the new section in `getNodes()`.
 */
export function computeAdoptionUpdatesForNewSection(
  sectionNode: Node,
  ghost: SectionPlacementGhost,
  nodes: Node[],
  getInternalNode: GetInternalNodeFn,
): SectionParentUpdate[] {
  const sectionBounds = boundsFromRect(
    ghost.position,
    ghost.width,
    ghost.height,
  );
  const nodesForAdoption = nodes.some((n) => n.id === sectionNode.id)
    ? nodes
    : [...nodes, sectionNode];
  const candidates = refineAdoptionCandidatesForSectionNesting(
    getAdoptionCandidates(nodes, sectionBounds, {
      excludeSectionId: sectionNode.id,
      getInternalNode,
    }),
    sectionNode,
    sectionBounds,
    nodesForAdoption,
    getInternalNode,
  );
  return buildParentUpdatesForAdoption(
    candidates,
    sectionNode,
    nodesForAdoption,
    getInternalNode,
  );
}

const ADOPTION_MAX_ATTEMPTS = 12;
/** Stop after this many consecutive frames with nothing left to adopt. */
const ADOPTION_STABLE_EMPTY_PASSES = 2;

/**
 * Run adoption after placement. Retries across animation frames until adoption
 * stabilizes or attempts are exhausted. Liveblocks sync can lag before the
 * section appears in `getNodes()`; React Flow measurement can lag before
 * intersecting nodes have reliable bounds.
 */
export function runAdoptionForNewSection(args: {
  sectionNode: Node;
  ghost: SectionPlacementGhost;
  getNodes: () => Node[];
  getInternalNode: GetInternalNodeFn;
  batchUpdateNodeParents: (updates: SectionParentUpdate[]) => void;
}): void {
  let attempt = 0;
  let stableEmptyPasses = 0;

  const tick = () => {
    const nodes = args.getNodes();
    const updates = computeAdoptionUpdatesForNewSection(
      args.sectionNode,
      args.ghost,
      nodes,
      args.getInternalNode,
    );
    if (updates.length > 0) {
      args.batchUpdateNodeParents(updates);
      stableEmptyPasses = 0;
    } else {
      stableEmptyPasses += 1;
    }

    attempt += 1;
    if (
      attempt < ADOPTION_MAX_ATTEMPTS &&
      stableEmptyPasses < ADOPTION_STABLE_EMPTY_PASSES
    ) {
      requestAnimationFrame(tick);
    }
  };

  requestAnimationFrame(tick);
}
